import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { MediaModel } from '../media.model';
import { isNullOrWhitespace } from '../functions';
import { Globals } from '../globals';
import { WalletStats, WalletTemplateModel } from '../wallet-template.model';
import { Honeycomb } from 'src/app/shared/honeycomb-api/honeycomb-api';
import { AppleWalletPassStyle } from '../wallet-passstyle-enum';
import { DOCUMENT } from '@angular/common';
import { TranslationService } from 'src/app/shared/translation.service';
import { WalletBarcodeType } from '../wallet-barcode-type.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { WalletTemplateField } from '../wallet-template-field.model';
import { DataType, WalletAppleField, WalletGoogleField, WalletTemplateDataSource } from '../wallet-template-enums';
import { CompanyViewModel } from '../../setting/company/company-view.model';

interface AppleSlotCell {
    pos: WalletAppleField;
    slotIndex: number;
    field: WalletTemplateField | null;
}


@Component({
    selector: 'hc-digital-card-apple',
    templateUrl: './digital-card-apple.component.html',
    styleUrls: ['./digital-card-apple.component.scss']
})

export class DigitalCardAppleComponent implements OnInit {

    walletBarcodeType = WalletBarcodeType;
    WalletAppleField = WalletAppleField;
    AppleWalletPassStyle = AppleWalletPassStyle;
    readonly genericHeaderSlots = [0];
    readonly genericSecondaryAuxiliarySlots = [0, 1, 2, 3];
    private _appleWalletFields: WalletTemplateField[] = [];

    @Input() public mediaModel: MediaModel;
    @Input() public walletTemplate: WalletTemplateModel;

    @Input() appleImageUrl: string | null = null;
    @Input() appleLogoUrl:  string | null = null;
    @Input() showBackSide = false;
    @Input() showContent: boolean = true;
    @Input() activeCompany: CompanyViewModel | null = null;
    @Input() fieldErrors: Record<string, boolean> | null = null;

    @Output() appleWalletFieldsChange = new EventEmitter<WalletTemplateField[]>();
    @Output() appleFieldSelected = new EventEmitter<{ field: WalletAppleField; slotIndex: number } | null>();

    @Input()
    set appleWalletFields(value: WalletTemplateField[]) {
        const normalizedResult = this.normalizeAppleWalletFieldsForCurrentStyle(value ?? []);
        this._appleWalletFields = normalizedResult.fields;

        if (normalizedResult.changed) {
            Promise.resolve().then(() => this.appleWalletFieldsChange.emit(this._appleWalletFields));
        }

        this.cd.markForCheck();
    }
    get appleWalletFields(): WalletTemplateField[] {
        return this._appleWalletFields;
    }


    activePos: WalletAppleField | null = null;
    activeSlotIndex = 0;
    editingPos: WalletAppleField | null = null;
    editingSlotIndex = 0;
    private editingCell?: HTMLElement | null;

    public rowCnt = 0;

    public appleStyle: string;

    private _globals: Globals;
    get getGlobals() {
        return this._globals;
    }

    private _sanitizer: DomSanitizer;
    get getSanitizer() {
        return this._sanitizer;
    }

    constructor(
        private globals: Globals,
        private sanitizer: DomSanitizer,
        private trans: TranslationService,
        private cd: ChangeDetectorRef,
        private elementRef: ElementRef,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this._globals = globals;
        this._sanitizer = sanitizer;
    }

    async ngOnInit() {
        this.appleStyle = AppleWalletPassStyle[this.walletTemplate.walletStyle];
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleEsc(_event: KeyboardEvent) {
        this.resetSelection();
    }

    @HostListener('document:click', ['$event'])
    handleDocumentClick(event: MouseEvent) {
        const target = event.target as Node | null;
        if (!target) return;
        const host = this.elementRef?.nativeElement as HTMLElement | null;
        const targetElement = target instanceof Element
            ? target
            : (target.parentElement as Element | null);

        if (!targetElement) {
            this.handleClickAway();
            return;
        }

        if (host?.contains(target)) {
            // Keep edit mode only when interacting with cell content; other clicks in card cancel edit.
            if (targetElement.closest('.cell, .cell-delete-button')) {
                return;
            }

            this.handleClickAway();
            return;
        }

        this.handleClickAway();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.walletTemplate?.currentValue) {
            this.appleStyle = AppleWalletPassStyle[this.walletTemplate.walletStyle];
            this.appleWalletFields = this._appleWalletFields;
        }
    }

    getImagePath(imgName: string): string {
        const baseUrl = this.document.baseURI;
        return `${baseUrl}img/${imgName}.png`;
    }

    getBarcodePreviewPath(): string | null {
        switch (this.mediaModel?.walletBarcodeType) {
            case WalletBarcodeType.QRCode:
                return 'wallet_qr';
            case WalletBarcodeType.PDF417:
                return 'wallet_pdf417';
            case WalletBarcodeType.Aztec:
                return 'wallet_aztec';
            case WalletBarcodeType.Code128:
                return 'wallet_code128';
            default:
                return null;
        }
    }

    getFieldsByPosition(pos: WalletAppleField): WalletTemplateField[] {
        return (this.appleWalletFields ?? [])
            .filter(f => f.appleField === pos)
            .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0));
    }

    getFieldByPosition(pos: WalletAppleField, slotIndex = 0): WalletTemplateField | null {
        const fields = this.getFieldsByPosition(pos);
        if (pos !== WalletAppleField.Secondary && pos !== WalletAppleField.Auxiliary) {
            return fields[0] ?? null;
        }

        return fields.find(f => (f.fieldOrder ?? 0) === slotIndex) ?? null;
    }

    getHeaderFields(): WalletTemplateField[] {
        return this.getFieldsByPosition(WalletAppleField.Header).slice(0, 1);
    }

    getPrimaryField(): WalletTemplateField | null {
        return this.getFieldByPosition(WalletAppleField.Primary, 0);
    }

    getSpecialFieldLabel(pos: WalletAppleField): string {
        if (pos === WalletAppleField.LogoText) {
            return 'logo text';
        }

        if (pos === WalletAppleField.BarcodeValue) {
            return 'barcode text';
        }

        return '';
    }

    lowercaseFirstLetter(val) {
        return String(val).charAt(0).toLowerCase() + String(val).slice(1);
    }

    getActualFieldValue(field: WalletTemplateField): string {
        let value = null;
        let sourceField = this.lowercaseFirstLetter(field.sourceField);
        if(field.dataSource === WalletTemplateDataSource.Media) {
            value = this.mediaModel?.[sourceField as keyof MediaModel] as string || '';
        }

        return value;
    }

    getLogoTextValue(): string {
        const field = this.getFieldByPosition(WalletAppleField.LogoText, 0);
        if (!field) {
            return '';
        }

        let value = this.getActualFieldValue(field);

        return value || field.defaultValueSourceField || field.sourceField || '';
    }

    getBarcodeTextValue(): string {
        const field = this.getFieldByPosition(WalletAppleField.BarcodeValue, 0);
        if (!field) {
            return '';
        }

        let value = this.getActualFieldValue(field);

        return value || field.defaultValueSourceField || field.sourceField || '';
    }

    getDisplayFieldValue(field: WalletTemplateField): string {
        let value = this.getActualFieldValue(field);

        return value || field.defaultValueSourceField || field.sourceField || '';
    }

    getBackSideFields(): WalletTemplateField[] {
        return (this.appleWalletFields ?? [])
            .filter(f => f.appleField === WalletAppleField.Back)
            .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0));
    }

    hasFieldError(fieldKey: string): boolean {
        return !!this.fieldErrors?.[fieldKey];
    }

    logCardDebugData(): void {
        console.log('Apple card debug dump', {
            mediaModel: this.mediaModel,
            walletTemplate: this.walletTemplate,
            appleWalletFields: this.appleWalletFields.filter(f => f.appleField === 4 || f.appleField === 5),
            appleStyle: this.appleStyle,
            showBackSide: this.showBackSide,
            showContent: this.showContent,
            activePos: this.activePos,
            activeSlotIndex: this.activeSlotIndex,
            editingPos: this.editingPos,
            editingSlotIndex: this.editingSlotIndex,
        });
    }

    onSpecialFieldClick(pos: WalletAppleField, ev: MouseEvent): void {
        ev.stopPropagation();

        const targetCell = ev.currentTarget instanceof HTMLElement ? ev.currentTarget : null;
        if (this.editingCell && targetCell && this.editingCell !== targetCell) {
            this.trySaveActiveEdit();
        }

        this.activePos = pos;
        this.activeSlotIndex = 0;
        this.editingPos = pos;
        this.editingSlotIndex = 0;
        this.editingCell = targetCell;
        this.appleFieldSelected.emit({ field: pos, slotIndex: 0 });
    }

    onSpecialFieldEnter(pos: WalletAppleField, value: string): void {
        const nextValue = value?.trim();

        if (!nextValue) {
            this.resetSelection();
            return;
        }

        const label = this.getSpecialFieldLabel(pos);
        const existing = this.getFieldByPosition(pos, 0);

        if (existing) {
            const updatedField = this.createUpdatedField(existing, pos, label, nextValue);
            this.appleWalletFields = (this.appleWalletFields ?? []).map(f => f === existing ? updatedField : f);
        } else {
            const field = this.createDefaultField(pos, label, nextValue, 0);
            this.appleWalletFields = [...(this.appleWalletFields ?? []), field];
        }

        this.appleWalletFieldsChange.emit(this.appleWalletFields);
        this.resetSelection();
    }

    getGenericSecondaryCells(): AppleSlotCell[] {
        return this.buildSlotCells(WalletAppleField.Secondary);
    }

    getGenericAuxiliaryCells(): AppleSlotCell[] {
        return this.buildSlotCells(WalletAppleField.Auxiliary);
    }

    getCouponSecondaryCells(): AppleSlotCell[] {
        return this.getGenericSecondaryCells();
    }

    getCouponAuxiliaryCells(): AppleSlotCell[] {
        return this.getGenericAuxiliaryCells();
    }

    getEventSecondaryCells(): AppleSlotCell[] {
        return this.buildSlotCells(WalletAppleField.Secondary);
    }

    getEventAuxiliaryCells(): AppleSlotCell[] {
        return this.buildSlotCells(WalletAppleField.Auxiliary);
    }

    hasEventSecondaryPlaceholder(): boolean {
        return this.getEventSecondaryCells().every(cell => !cell.field);
    }

    hasEventAuxiliaryPlaceholder(): boolean {
        return this.getEventAuxiliaryCells().every(cell => !cell.field);
    }

    getStoreSecondaryCells(): AppleSlotCell[] {
        return this.getGenericSecondaryCells();
    }

    getStoreAuxiliaryCells(): AppleSlotCell[] {
        return this.getGenericAuxiliaryCells();
    }

    getStoreCombinedFields(): Array<{ pos: WalletAppleField; slotIndex: number; field: WalletTemplateField }> {
        const secondary = this.getStoreSecondaryCells().filter(cell => !!cell.field).map(cell => ({
            pos: WalletAppleField.Secondary,
            slotIndex: cell.slotIndex,
            field: cell.field as WalletTemplateField,
        }));
        const auxiliary = this.getStoreAuxiliaryCells().filter(cell => !!cell.field).map(cell => ({
            pos: WalletAppleField.Auxiliary,
            slotIndex: cell.slotIndex,
            field: cell.field as WalletTemplateField,
        }));

        return [...secondary, ...auxiliary];
    }

    hasGenericSecondaryPlaceholder(): boolean {
        return this.getGenericSecondaryCells().every(cell => !cell.field);
    }

    hasGenericAuxiliaryPlaceholder(): boolean {
        return this.getGenericAuxiliaryCells().every(cell => !cell.field);
    }

    getGenericSecondaryFields(): WalletTemplateField[] {
        return this.getGenericSecondaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    getGenericAuxiliaryFields(): WalletTemplateField[] {
        return this.getGenericAuxiliaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    getCouponSecondaryFields(): WalletTemplateField[] {
        return this.getCouponSecondaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    getCouponAuxiliaryFields(): WalletTemplateField[] {
        return this.getCouponAuxiliaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    hasCouponSecondaryPlaceholder(): boolean {
        return this.getCouponSecondaryCells().every(cell => !cell.field);
    }

    hasCouponAuxiliaryPlaceholder(): boolean {
        return this.getCouponAuxiliaryCells().every(cell => !cell.field);
    }

    getStoreSecondaryFields(): WalletTemplateField[] {
        return this.getStoreSecondaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    getStoreAuxiliaryFields(): WalletTemplateField[] {
        return this.getStoreAuxiliaryCells().map(cell => cell.field).filter((field): field is WalletTemplateField => !!field);
    }

    hasStorePlaceholder(): boolean {
        return this.getStoreSecondaryCells().every(cell => !cell.field)
            && this.getStoreAuxiliaryCells().every(cell => !cell.field);
    }

    isCellActive(pos: WalletAppleField, slotIndex = 0): boolean {
        return this.isEditing(pos, slotIndex);
    }

    isEditing(pos: WalletAppleField, slotIndex = 0): boolean {
        return this.editingPos === pos && this.editingSlotIndex === slotIndex;
    }

    onRowClick(ev: MouseEvent): void {
        ev.stopPropagation();

        const rawTarget = ev.target as Node | null;
        if (!rawTarget) return;

        const targetElement = rawTarget instanceof Element
            ? rawTarget
            : rawTarget.parentElement;
        if (!targetElement) return;

        const cell = targetElement.closest('.cell') as HTMLElement | null;
        if (!cell) return;
        const posAttr = cell.getAttribute('data-pos');
        if (!posAttr) return;
        const pos = Number(posAttr) as WalletAppleField;
        const slotAttr = cell.getAttribute('data-slot');
        const slotIndex = Number(slotAttr ?? '0');

        if (this.editingCell && this.editingCell !== cell) {
            this.trySaveActiveEdit();
        }

        // Two-state behavior: clicking a cell always enters/stays in edit mode for that cell.
        this.activePos = pos;
        this.activeSlotIndex = slotIndex;
        this.editingPos = pos;
        this.editingSlotIndex = slotIndex;
        this.editingCell = cell;
        this.appleFieldSelected.emit({ field: pos, slotIndex });
    }

    onCellEnter(pos: WalletAppleField, header: string, value: string, slotIndex = 0): void {
        const h = header?.trim();
        const v = value?.trim();
        if (!h || !v) {
            this.resetSelection();
            return;
        }

        const index = Math.max(0, slotIndex);
        const fieldToUpdate = this.getFieldByPosition(pos, index);

        if (!fieldToUpdate && !this.canAddFieldAtPosition(pos)) {
            this.resetSelection();
            return;
        }

        if (fieldToUpdate) {
            const updatedField = this.createUpdatedField(fieldToUpdate, pos, h, v);
            this.appleWalletFields = (this.appleWalletFields ?? []).map(f => f === fieldToUpdate ? updatedField : f);
        } else {
            const field = this.createDefaultField(pos, h, v, index);
            this.appleWalletFields = [...(this.appleWalletFields ?? []), field];
        }

        this.appleWalletFieldsChange.emit(this.appleWalletFields);
        this.resetSelection();
    }

    clearCell(pos: WalletAppleField, slotIndex = 0, ev?: MouseEvent): void {
        ev?.stopPropagation();

        if (pos === WalletAppleField.Header || pos === WalletAppleField.LogoText || pos === WalletAppleField.Primary || pos === WalletAppleField.BarcodeValue) {
            this.appleWalletFields = (this.appleWalletFields ?? []).filter(f => f.appleField !== pos);
        } else {
            this.appleWalletFields = (this.appleWalletFields ?? []).filter(f => !(f.appleField === pos && (f.fieldOrder ?? 0) === slotIndex));
        }

        this.appleWalletFieldsChange.emit(this.appleWalletFields);

        if (pos === WalletAppleField.LogoText || pos === WalletAppleField.BarcodeValue) {
            this.resetSelection();
            return;
        }

        // Stay in edit mode for the same cell after delete so user can immediately type a new value.
        this.activePos = pos;
        this.activeSlotIndex = slotIndex;
        this.editingPos = pos;
        this.editingSlotIndex = slotIndex;
        this.editingCell = null;
        this.appleFieldSelected.emit({ field: pos, slotIndex });
    }

    private resetSelection(): void {
        this.editingPos = null;
        this.editingSlotIndex = 0;
        this.editingCell = null;
        this.activePos = null;
        this.activeSlotIndex = 0;
        this.appleFieldSelected.emit(null);
    }

    private clearEditingOnly(): void {
        this.editingPos = null;
        this.editingSlotIndex = 0;
        this.editingCell = null;
    }

    private handleClickAway(): void {
        if (this.trySaveActiveEdit()) {
            return;
        }

        this.clearEditingOnly();

        if (this.activePos != null) {
            this.appleFieldSelected.emit({ field: this.activePos, slotIndex: this.activeSlotIndex });
        }
    }

    private trySaveActiveEdit(): boolean {
        if (this.editingPos == null || !this.editingCell) {
            return false;
        }

        const singleValueInput = this.editingCell.querySelector('input[data-field="single-value"]') as HTMLInputElement | null;
        if (singleValueInput
            && (this.editingPos === WalletAppleField.LogoText || this.editingPos === WalletAppleField.BarcodeValue)) {
            const value = singleValueInput.value?.trim();
            if (isNullOrWhitespace(value)) {
                return false;
            }

            this.onSpecialFieldEnter(this.editingPos, value);
            return true;
        }

        const headerInput = this.editingCell.querySelector('input[data-field="header"]') as HTMLInputElement | null;
        const valueInput = this.editingCell.querySelector('input[data-field="value"]') as HTMLInputElement | null;

        if (!headerInput || !valueInput) {
            return false;
        }

        const header = headerInput.value?.trim();
        const value = valueInput.value?.trim();

        if (!header || !value) {
            return false;
        }

        this.onCellEnter(this.editingPos, header, value, this.editingSlotIndex);
        return true;
    }

    onCellEscape(_pos: WalletAppleField, _cellRef: HTMLElement, _ev: KeyboardEvent): void {
        this.resetSelection();
    }

    private buildSlotCells(pos: WalletAppleField, totalSlots = 4): AppleSlotCell[] {
        return Array.from({ length: totalSlots }, (_, slotIndex) => ({
            pos,
            slotIndex,
            field: this.getFieldByPosition(pos, slotIndex),
        }));
    }

    private canAddFieldAtPosition(pos: WalletAppleField): boolean {
        const style = this.walletTemplate?.walletStyle;

        if (pos === WalletAppleField.Header) {
            return this.getFieldsByPosition(WalletAppleField.Header).length < 1;
        }

        if (pos === WalletAppleField.LogoText) {
            return this.getFieldsByPosition(WalletAppleField.LogoText).length < 1;
        }

        if (pos === WalletAppleField.Primary) {
            return this.getFieldsByPosition(WalletAppleField.Primary).length < 1;
        }

        if (pos === WalletAppleField.BarcodeValue) {
            return this.getFieldsByPosition(WalletAppleField.BarcodeValue).length < 1;
        }

        if (pos === WalletAppleField.Secondary || pos === WalletAppleField.Auxiliary) {
            if (style === AppleWalletPassStyle.EventTicket) {
                if (pos === WalletAppleField.Secondary) {
                    return this.getFieldsByPosition(WalletAppleField.Secondary).length < 4;
                }

                return this.getFieldsByPosition(WalletAppleField.Auxiliary).length < 4;
            }

            const combinedCnt = this.getFieldsByPosition(WalletAppleField.Secondary).length
                + this.getFieldsByPosition(WalletAppleField.Auxiliary).length;
            return combinedCnt < 4;
        }

        return true;
    }

    private createUpdatedField(existing: WalletTemplateField, pos: WalletAppleField, header: string, value: string): WalletTemplateField {
        return {
            ...existing,
            appleField: pos,
            label: header,
            sourceField: '',
            defaultValueSourceField: value,
            defaultValueLabel: header,
        } as WalletTemplateField;
    }

    private createDefaultField(pos: WalletAppleField, header: string, value: string, fieldOrder = 0): WalletTemplateField {
        return {
            walletTemplateFieldID: 0,
            walletTemplateID: this.walletTemplate?.walletTemplateID ?? 0,
            fieldOrder,
            dataType: DataType.Text,
            dataSource: WalletTemplateDataSource.None,
            label: header,
            sourceField: '',
            labelDataSource: WalletTemplateDataSource.None,
            defaultValueDataSource: WalletTemplateDataSource.None,
            defaultValueSourceField: value,
            defaultValueLabel: header,
            defaultValueLabelDataSource: WalletTemplateDataSource.None,
            appleField: pos,
            googleField: WalletGoogleField.Undefined,
            changeMessage: '',
            googleClassVariantField: false,
            created: new Date()
        } as WalletTemplateField;
    }

    private normalizeAppleWalletFieldsForCurrentStyle(fields: WalletTemplateField[]): { fields: WalletTemplateField[]; changed: boolean } {
        const normalized = [...(fields ?? [])];
        let changed = false;

        if (this.walletTemplate?.walletStyle === AppleWalletPassStyle.StoreCard) {
            const storeCardResult = this.normalizeStoreCardFields(normalized);
            changed = changed || storeCardResult.changed;
            normalized.splice(0, normalized.length, ...storeCardResult.fields);
        }

        const positionsToNormalize = [WalletAppleField.Secondary, WalletAppleField.Auxiliary];

        for (const pos of positionsToNormalize) {
            const candidates = normalized
                .map((field, index) => ({ field, index }))
                .filter(item => item.field.appleField === pos)
                .sort((a, b) => {
                    const orderDiff = this.normalizeFieldOrder(a.field.fieldOrder) - this.normalizeFieldOrder(b.field.fieldOrder);
                    return orderDiff !== 0 ? orderDiff : a.index - b.index;
                });

            const used = new Set<number>();

            for (const item of candidates) {
                const currentOrder = this.normalizeFieldOrder(item.field.fieldOrder);
                let targetOrder = currentOrder;

                if (used.has(targetOrder)) {
                    targetOrder = 0;
                    while (used.has(targetOrder)) {
                        targetOrder++;
                    }
                }

                used.add(targetOrder);

                if (item.field.fieldOrder !== targetOrder) {
                    normalized[item.index] = { ...item.field, fieldOrder: targetOrder };
                    changed = true;
                }
            }
        }

        return { fields: normalized, changed };
    }

    private normalizeStoreCardFields(fields: WalletTemplateField[]): { fields: WalletTemplateField[]; changed: boolean } {
        const normalized = [...(fields ?? [])];
        const candidates = normalized
            .map((field, index) => ({ field, index }))
            .filter(item => item.field.appleField === WalletAppleField.Secondary || item.field.appleField === WalletAppleField.Auxiliary)
            .sort((a, b) => {
                const fieldPosDiff = (a.field.appleField ?? 0) - (b.field.appleField ?? 0);
                if (fieldPosDiff !== 0) {
                    return fieldPosDiff;
                }

                const orderDiff = this.normalizeFieldOrder(a.field.fieldOrder) - this.normalizeFieldOrder(b.field.fieldOrder);
                return orderDiff !== 0 ? orderDiff : a.index - b.index;
            });

        let changed = false;

        candidates.forEach((item, slotIndex) => {
            const targetOrder = slotIndex;
            if (item.field.appleField !== WalletAppleField.Secondary || this.normalizeFieldOrder(item.field.fieldOrder) !== targetOrder) {
                normalized[item.index] = {
                    ...item.field,
                    appleField: WalletAppleField.Secondary,
                    fieldOrder: targetOrder,
                } as WalletTemplateField;
                changed = true;
            }
        });

        return { fields: normalized, changed };
    }

    private normalizeFieldOrder(value: any): number {
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return 0;
        }

        return Math.max(0, Math.floor(num));
    }

    trackByField = (_: number, field: WalletTemplateField) => `${field.walletTemplateFieldID}-${field.appleField}-${field.fieldOrder}`;
    trackBySlotCell = (_: number, cell: AppleSlotCell) => `${cell.pos}-${cell.slotIndex}-${cell.field?.walletTemplateFieldID ?? 'empty'}`;
    trackByStoreCombined = (_: number, item: { pos: WalletAppleField; slotIndex: number; field: WalletTemplateField }) => `${item.pos}-${item.slotIndex}-${item.field.walletTemplateFieldID}`;
    trackByIndex = (index: number) => index;
    trackByItem = (_: number, v: string) => v;
}
